const _ = require("lodash");
const PS = require("./util/ParserState");
const Helpers = require("./util/ParserHelpers");
const RenderHelpers = require("./util/RenderHelpers");
const Macros = require("./Macros");
const PostParser = require("./PostParser");
const DelimParser = require("./util/Delim");
const Operators = require("./Operators");
const NumberUtil = require("../../../shared/Numbers");
const ColorUtil = require("../util/ColorUtil");

const parseTable = require("./ParserTable").parseTable;
const parseOptree = require("./ParserOptree").parseOptree;
const parseNumberline = require("./ParserNumberline").parseNumberline;
const parseImage = require("./ParserImage").parseImage;
const parseDiagram = require("./Diagram").parseDiagram;

const CommandIndex = require("./CommandIndex");
// If a command needs access to a function in this file (Parser.js), make sure it is in the ps instance.

// Alias commonly used helpers
const {
	findArguments,
	findSomeArguments,
	findOptionalArgument,
	findOptionalArguments,
	splitArgIntoText,
	splitArgIntoNumbers,
	splitArgIntoMathNodes,
	splitArgIntoNonmathNodes,
	makeNonmathCommandNode,
	makeMathCommandNode,
	makeNonmathArgNode,
	makeMathArgNode,
	makeFormattedText,
	keepEdgeSpaces,
	parseAndIgnoreSpace,
	parseStartOfMath,
} = Helpers;

// Entry points for parsing, also the module exports. Pass batex in. The return
// is an object with keys tree, errors. tree is the root node of the parse tree,
// which may be false if parsing failed completely. errors is an array of
// objects with message, pos keys.

const parseEntryNonmath = function(batex, data) {
	if (typeof batex === "number") {
		batex = "" + batex;
	} else if (typeof batex !== "string") {
		throw new Error("CanvasMath: Expected batex to be a string, got " + batex);
	}

	data = _.extend(
		{
			parseMath: parseMath,
			parseNonmath: parseNonmath,
		},
		data
	);

	const ps = new PS(batex, 0, data);
	const nodes = ps.parseNonmath(ps);
	const tree = nodes && {
		type: "paragraph",
		children: [
			{
				type: "wrapping",
				children: nodes,
				isWrapped: false,
			},
		],
	};
	return {
		tree: PostParser.transform(tree),
		errors: ps.errors,
	};
};

const parseEntryMath = function(batex, data) {
	if (typeof batex !== "string") {
		throw new Error("CanvasMath: Expected batex to be a string, got " + batex);
	}

	data = _.extend(
		{
			parseMath: parseMath,
			parseNonmath: parseNonmath,
		},
		data
	);

	const ps = new PS(batex, 0, data);
	const nodes = ps.parseMath(ps);
	const tree = nodes && {
		type: "math",
		children: nodes,
	};
	return {
		tree: PostParser.transform(tree),
		errors: ps.errors,
	};
};

// Other than the entry points, all parsing functions use the ParserState object
// defined in the module PS.
// Each parsing function goes through some amount of the batex in the parser
// state, moves the parser state past all of the processed batex with any errors
// recorded, and returns an array of nodes to go into the parse tree. Think of
// them as functions with three returns: the array of nodes parsed, the new
// parser position, and the found errors. (The last two come in the form of
// mutations to the parser state that was passed in.)

//
// Nonmath parsing
//

const nonmathDelimTable = [
	{
		type: "break",
		begin: "\n",
	},
	{
		type: "break",
		begin: "\\\\",
	},
	{
		type: "space",
		begin: "\\ ",
	},
	{
		type: "escaped",
		begin: "\\$",
	},
	{
		type: "escaped",
		begin: "\\{",
	},
	{
		type: "escaped",
		begin: "\\}",
	},
	{
		type: "escaped",
		begin: "\\&",
	},
	{
		type: "blockMath",
		begin: "$$",
	},
	{
		type: "inlineMath",
		begin: "$",
	},
	{
		type: "blockMath",
		begin: "\\[",
	},
	{
		type: "inlineMath",
		begin: "\\(",
	},
	{
		// All backslashes not followed by an alphabetic character should be handled
		// in one of the above cases. If we get here and the next character is not
		// alphabetic, it will result in an error.
		type: "command",
		begin: "\\",
	},
];
// This function processes all of the provided text, assuming the beginning is
// nonmath.
const parseNonmath = function(ps) {
	if (typeof ps === "string") {
		ps.pushError(
			"Passed string to parseNonmath; use parseNonmathString instead"
		);
		ps = PS.convert(ps);
	}
	let posNow = ps.pos;
	const nodeResults = [];
	while (!ps.done()) {
		nodeResults.push(parseNonmathOne(ps));
		if (posNow === ps.pos) {
			ps.pushError("Had to break out of infinite parsing loop.");
			break;
		}
		posNow = ps.pos;
	}
	return Helpers.mergeTextNodes(_.flatten(nodeResults));
};
// This function processes text up to the first special nonmath character.
const parseNonmathOne = function(ps) {
	ps = PS.convert(ps);
	const delim = DelimParser.findNextDelim(ps, nonmathDelimTable);
	if (!delim) {
		// No special characters left; parse all as text.
		return parseNonmathText(ps);
	}

	// Handle all text up to the found delimiter
	const psForText = ps.upTo(delim.start);
	const textNodes = parseNonmathText(psForText);

	// Handle the delimiter itself.
	const type = delim.block.type;
	const bd = delim.block.begin;
	let delimNodes;
	if (type === "break") {
		ps.pos = delim.end;
		delimNodes = [{type: "lineBreak", manual: bd === "\\\\"}];
		parseAndIgnoreSpace(ps, {newlineLimit: 0});
	} else if (type === "space") {
		ps.pos = delim.end;
		delimNodes = [{type: "space", manual: true}];
	} else if (type === "escaped") {
		ps.pos = delim.end;
		delimNodes = parseNonmathText(
			ps.withText(delim.block.text[1]).pipeErrorsAt(delim.start)
		);
	} else if (type === "blockMath" && bd === "$$") {
		ps.pos = delim.start;
		delimNodes = parseStartOfMath(ps, "blockMath", "$$", "$$");
		parseAndIgnoreSpace(ps, {newlineLimit: 1});
	} else if (type === "inlineMath" && bd === "$") {
		ps.pos = delim.start;
		delimNodes = parseStartOfMath(ps, "math", "$", "$");
	} else if (type === "blockMath" && bd === "\\[") {
		ps.pos = delim.start;
		delimNodes = parseStartOfMath(ps, "blockMath", "\\[", "\\]");
		parseAndIgnoreSpace(ps, {newlineLimit: 1});
	} else if (type === "inlineMath" && bd === "\\(") {
		ps.pos = delim.start;
		delimNodes = parseStartOfMath(ps, "math", "\\(", "\\)");
	} else if (type === "command") {
		ps.pos = delim.start;
		delimNodes = parseCommand(ps, false);
	}
	return [].concat(textNodes, delimNodes);
};

// This function processes all of the provided text as normal text with no
// special characters.
const parseNonmathText = function(ps) {
	ps = PS.convert(ps);
	let text = ps.text.slice(ps.pos);
	// Helpers.checkForFourDigits(ps, text);

	const nodes = [];
	let space;
	while (text.length) {
		space = /\s+/.exec(text);
		if (space) {
			if (space.index) {
				nodes.push({type: "text", text: text.slice(0, space.index)});
			}
			nodes.push({type: "space"});
			text = text.slice(space.index + space[0].length);
		} else {
			nodes.push({type: "text", text: text});
			text = "";
		}
	}

	ps.pos = ps.text.length;
	return nodes;
};

// Convenience helper for parsing a fixed string literal. Clones the passed
// ps and replaces its text with what was passed, then parses as nonmath.
const parseNonmathString = function(ps, text) {
	return parseNonmath(ps.withText(text));
};

//
// Math parsing
//

let mathDelimTable = [
	{
		type: "punctuation",
		begin: ",",
	},
	{
		type: "punctuation",
		begin: ".",
	},
	{
		type: "punctuation",
		begin: ";",
	},
	{
		type: "punctuation",
		begin: ":",
	},
	{
		type: "break",
		begin: "\\\\",
	},
	{
		type: "escaped",
		begin: "\\$",
	},
	{
		type: "escaped",
		begin: "\\{",
	},
	{
		type: "escaped",
		begin: "\\}",
	},
	{
		type: "escaped",
		begin: "\\&",
	},
	{
		type: "space",
		begin: "\\ ",
	},
	Operators.list.map(op => ({type: "operator", begin: op})),
	{
		type: "superscript",
		begin: "^",
	},
	{
		// All backslashes not followed by an alphabetic character should be handled
		// in one of the above cases. If we get here and the next character is not
		// alphabetic, it will result in an error.
		type: "command",
		begin: "\\",
	},
];
// The above contains a nested array with the operators; we want a flat array.
mathDelimTable = _.flatten(mathDelimTable);

// This function processes all of the provided text as math.
const parseMath = function(ps) {
	if (typeof ps === "string") {
		ps.pushError("Passed string to parseMath; use parseMathString instead");
		ps = PS.convert(ps);
	}
	let posNow = ps.pos;
	const nodeResults = [];
	while (!ps.done()) {
		nodeResults.push(parseMathOne(ps));
		if (posNow === ps.pos) {
			ps.pushError("Had to break out of infinite parsing loop.");
			break;
		}
		posNow = ps.pos;
	}
	return Helpers.mergeTextNodes(_.flatten(nodeResults));
};
// This function processes text up to the first special math character.
const parseMathOne = function(ps) {
	ps = PS.convert(ps);
	const delim = DelimParser.findNextDelim(ps, mathDelimTable);
	if (!delim) {
		// No special characters left; parse all as text.
		return parseMathText(ps);
	}

	// Handle all text up to the found delimiter
	const psForText = ps.upTo(delim.start);
	const textNodes = parseMathText(psForText);

	// Handle the delimiter itself.
	const type = delim.block.type;
	const bd = delim.block.begin;
	let delimNodes;
	if (type === "break") {
		ps.pos = delim.end;
		delimNodes = []; // We do nothing for instances of \\ in math.
	} else if (type === "escaped") {
		ps.pos = delim.end;
		delimNodes = parseMathText(
			ps.withText(delim.block.text[1]).pipeErrorsAt(delim.start)
		);
	} else if (type === "space") {
		ps.pos = delim.end;
		delimNodes = [{type: "space", manual: true}];
	} else if (type === "punctuation") {
		ps.pos = delim.end;
		delimNodes = [
			{
				type: "text",
				text: bd + (/\s/.test(ps.text[ps.pos]) ? " " : ""),
			},
		];
	} else if (type === "operator") {
		ps.pos = delim.end;
		delimNodes = [
			{
				type: "operator",
				text: bd,
				isUnary: Operators.isNextUnary(ps.text, delim.start),
			},
		];
	} else if (type === "superscript") {
		ps.pos = delim.end;
		// PostParser turns this into a superscript node attached to previous
		// child.
		delimNodes = makeMathCommandNode(
			"superscriptUnattached",
			findArguments(ps, 1)
		);
	} else if (type === "command") {
		ps.pos = delim.start;
		delimNodes = parseCommand(ps, true);
	}
	return [].concat(textNodes, delimNodes);
};

// This function processes all of the provided text as math text with no
// special characters. Variable nodes are produced here.
const parseMathText = function(ps) {
	let text = ps.text.slice(ps.pos);

	// Helpers.checkForFourDigits(ps, text);

	text = text.replace(/\s/g, "");
	const nodes = [];

	// This could be reorganized to be part of the math delims if the
	// DelimParser could be made to support regex in the delimiters, but it's
	// not worth it to do so.
	const variablePattern = /[A-Za-z]+/;
	let match;
	while ((match = variablePattern.exec(text))) {
		if (match.index > 0) {
			nodes.push({
				type: "text",
				text: text.slice(0, match.index),
			});
		}
		nodes.push({
			type: "variable",
			children: [
				{
					type: "text",
					text: match[0],
				},
			],
		});
		text = text.slice(match.index + match[0].length);
	}
	if (text) {
		nodes.push({
			type: "text",
			text: text,
		});
	}

	ps.pos = ps.text.length;
	return nodes;
};

// Convenience helper for parsing a fixed string literal. Clones the passed
// ps and replaces its text with what was passed, then parses as math.
const parseMathString = function(ps, text) {
	return parseMath(ps.withText(text));
};

//
// Commands and environments
//

// The provided parser state should be at the position of the backslash.
// This function results in an error if the next character is not alphabetic;
// nonalphabetic commands should be processed elsewhere.
const parseCommand = function(ps, inMath) {
	// Special case error: backslash at very end.
	if (ps.text.length <= ps.pos + 1) {
		ps.pushError("Unrecognized '\\' at end of input.");
		ps.pos += 1;
		return [
			{
				type: "error",
				name: "[EOI]",
			},
		];
	}

	const namePattern = /^\\([a-zA-Z]+)/;
	const nameMatch = namePattern.exec(ps.text.slice(ps.pos));

	// commandName is only valid if nameMatch exists, but this fallback of taking
	// the next character regardless makes error handling more convenient.
	const commandName = nameMatch ? nameMatch[1] : ps.text[ps.pos + 1];
	let handler = getCommandHandler(ps, inMath, !!nameMatch, commandName);
	// If no handler was found, use one that always returns an error.
	handler = handler || (() => false);

	ps.pos += 1 + commandName.length;
	// If the command handler fails or does not exist, return an error node.
	return (
		handler(ps) || [
			{
				type: "error",
				name: commandName,
			},
		]
	);
};

// Given a parser state moved just after a \begin command, parse the
// environment it begins.
const parseEnvironment = function(ps, inMath) {
	ps = PS.convert(ps);

	const namePos = ps.pos + 1;
	let envName = findArguments(ps, 1, {requireBrace: true, noSpace: true});
	if (!envName) {
		return false;
	}
	envName = envName[0].getRest();
	// Object used as an error if something goes wrong, in place of return false.
	const errorNode = {
		type: "error",
		name: "begin{" + envName + "}",
	};

	const beginCommand = "\\begin{" + envName + "}";
	const endCommand = "\\end{" + envName + "}";

	const endPos = Helpers.findNestedDelim(
		ps,
		{
			begin: beginCommand,
			escape: "\\",
		},
		{
			begin: endCommand,
			escape: "\\",
		}
	);

	if (endPos === -1) {
		ps.pushError(
			"Matching '\\end' for environment '" + envName + "' not found."
		);
		return errorNode;
	}
	const envBody = ps.text.slice(ps.pos, endPos);

	let handler = inMath
		? mathEnvironmentHandlers[envName]
		: nonmathEnvironmentHandlers[envName];
	handler = handler || getCustomEnvironmentHandler(ps, envName);
	if (!handler) {
		ps.pushError("Environment '" + envName + "' unrecognized.", namePos);
		ps.pos = endPos + endCommand.length;
		return errorNode;
	}

	const psForEnv = ps.withText(envBody).pipeErrorsAt(ps.pos);
	ps.pos = endPos + endCommand.length;
	parseAndIgnoreSpace(ps, {newlineLimit: 1});
	return handler(psForEnv) || errorNode;
};

// Get the handler for a command name
const getCommandHandler = (function() {
	// Objects used to remember errors that have been flagged to prevent
	// duplicates of the same error.
	const badNonmathNamesSoFar = {};
	const badMathNamesSoFar = {};

	return function(ps, inMath, foundAName, commandName) {
		const badTable = inMath ? badMathNamesSoFar : badNonmathNamesSoFar;
		const handlerTable = inMath ? mathCommandHandlers : nonmathCommandHandlers;
		const wrongHandlerTable = inMath
			? nonmathCommandHandlers
			: mathCommandHandlers;
		const macroHandlerTable = inMath ? Macros.math : Macros.nonmath;

		const isKnownToBeBad = badTable[commandName];
		let handler = handlerTable[commandName];
		handler = handler || getCustomCommandHandler(ps, commandName);
		handler = handler || macroHandlerTable[commandName];

		// isKnownToBeBad and badTable ensures we only show this error once.
		if (
			!isKnownToBeBad &&
			handlerTable[commandName] &&
			macroHandlerTable[commandName]
		) {
			badTable[commandName] = true;
			ps.pushError(
				"Command '\\" +
					commandName +
					"' has both a basic definition " +
					"and a macro. The macro was ignored."
			);
		}

		if (!handler && wrongHandlerTable[commandName]) {
			ps.pushError(
				"Command '\\" +
					commandName +
					"' must be used " +
					(inMath ? "outside" : "inside") +
					" of math mode."
			);
			return false;
		} else if (!foundAName || !handler) {
			ps.pushError("Unrecognized command '\\" + commandName + "'.");
			return false;
		}

		return handler;
	};
})();

// Object mapping command names (strings of alphabetic characters only) to
// functions taking a parser state, which has been moved past the end of the
// command name. For convenience of writing these, the parser state is assumed
// to be always an object and never a string (so no need for PS.convert).
// These functions may return false to signify command parsing failed. In this
// case the parser state is moved past all the characters that were part of the
// invalid command, and errors are already recorded.
const nonmathCommandHandlers = {
	center: function(ps) {
		const nodes = makeNonmathCommandNode("center", findArguments(ps, 1));
		parseAndIgnoreSpace(ps, {newlineLimit: 1});
		return nodes;
	},
	indent: function(ps) {
		return [{type: "indent", manual: true}];
	},
	blocklist: function(ps) {
		const align = findOptionalArgument(ps);
		let children = parseNonmath(findArguments(ps, 1)[0]);
		children = children.filter(n => n.type !== "lineBreak");
		const nodes = [
			{
				type: "blockList",
				alignment: align ? align.getRest() : false,
				children: children,
			},
		];
		parseAndIgnoreSpace(ps, {newlineLimit: 1});
		return nodes;
	},
	parbox: function(ps) {
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		let widthCoeffString = args[0].getRest().trim();
		let noTooSmallErrors = false;
		if (widthCoeffString.indexOf("!") > -1) {
			noTooSmallErrors = true;
			widthCoeffString = widthCoeffString.replace(/\!/g, "");
		}
		const widthCoeff = parseFloat(widthCoeffString);
		if (!_.isFinite(widthCoeff)) {
			ps.pushError("The first argument of parbox must be a real number.");
		} else if (!noTooSmallErrors && widthCoeff < 24) {
			ps.pushError(
				"parbox coefficient less than 24 makes for bad wordwrapping. " +
					'(Add "!" after the number to suppress this error.)'
			);
		}
		return [
			{
				type: "parbox",
				widthCoeff,
				children: [
					{
						type: "wrapping",
						children: parseNonmath(args[1]),
					},
				],
			},
		];
	},
	begin: function(ps) {
		return parseEnvironment(ps, false);
	},
	end: function(ps) {
		// Error: this should be found by parseEnvironment.
		const commandPos = ps.pos - 4;
		const args = findArguments(ps, 1);
		if (!args) {
			ps.pushError("Invalid '\\end' command.", commandPos);
		} else {
			ps.pushError(
				"Found '\\end{" + args[0].getRest() + "}' before '\\begin'.",
				commandPos
			);
		}
		return false;
	},
	...CommandIndex.parsers.nonmathCommands,
};
const mathCommandHandlers = {
	light: function(ps) {
		return makeMathCommandNode("light", findArguments(ps, 1));
	},
	text: function(ps) {
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const node = makeNonmathArgNode(args[0], {noTrim: true});
		return keepEdgeSpaces(node);
	},
	neg: function(ps) {
		return [
			{
				type: "operator",
				text: "-",
				isUnary: true,
			},
		];
	},
	minus: function(ps) {
		return [
			{
				type: "operator",
				text: "-",
				isUnary: false,
			},
		];
	},
	plus: function(ps) {
		return [
			{
				type: "operator",
				text: "+",
				isUnary: false,
			},
		];
	},
	quad: function(ps) {
		const optArg = findOptionalArgument(ps);
		const scaleFactor = optArg ? parseFloat(optArg.getRest()) : 1;
		return [
			{
				type: "quad",
				scale: scaleFactor || 1,
			},
		];
	},
	frac: function(ps) {
		return makeMathCommandNode("fraction", findArguments(ps, 2));
	},
	abs: function(ps) {
		return makeMathCommandNode("absoluteValue", findArguments(ps, 1));
	},
	paren: function(ps) {
		return makeMathCommandNode("parentheses", findArguments(ps, 1));
	},
	sqrt: function(ps) {
		const nthRoot = findOptionalArgument(ps);
		return makeMathCommandNode("sqrt", findArguments(ps, 1), {
			nthRoot: nthRoot ? nthRoot.text : false,
		});
	},
	decmove: function(ps) {
		const optArg = findOptionalArgument(ps);
		const optArg2 = findOptionalArgument(ps);
		const optArgText = optArg && optArg.getRest().trim();
		const optArg2Text = optArg2 && optArg2.getRest().trim();
		const arrow = _.includes([optArgText, optArg2Text], "arrow");
		const fromRight = parseInt(optArgText) || parseInt(optArg2Text);
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const convertArgs = PS.convert(args[0]);
		const text = convertArgs.getRest();
		const digits = text ? text.split("").map(a => a.trim()) : [];
		return [
			{
				type: "decimalMovement",
				digits,
				arrow,
				fromRight,
				children: [],
			},
		];
	},
	decarrow: function(ps) {
		const optArg = findOptionalArgument(ps);
		const lineArrows = optArg ? optArg.getRest().trim() === "move" : false;
		const args = findArguments(ps, 2);
		if (!args || args.length < 2) {
			return false;
		}
		const numbers = [args[0].text.trim(), 0];
		const power = parseInt(args[1].text.trim());
		const mult = _.round(Math.pow(10, power), -power);
		if (numbers[0].indexOf(".") === -1) {
			numbers[0] += ".";
		}
		const periodIndices = [numbers[0].indexOf("."), -1];
		const powersOfTen = [];
		if (lineArrows) {
			numbers[1] = _.round(
				parseFloat(numbers[0]) * mult,
				numbers[0].length - periodIndices[0] - power
			).toString();
			if (numbers[1].indexOf(".") === -1) {
				numbers[1] += ".";
			}
			periodIndices[1] = numbers[1].indexOf(".");

			const maxPeriodIndex = Math.max(periodIndices[0], periodIndices[1]);
			const numBlanks =
				maxPeriodIndex +
				Math.max(
					numbers[0].length - periodIndices[0],
					numbers[1].length - periodIndices[1]
				);
			for (let pos = 0; pos < numBlanks; pos++) {
				if (pos !== maxPeriodIndex) {
					const pow = maxPeriodIndex - pos + (maxPeriodIndex > pos ? -1 : 0);
					const powerOfTen = NumberUtil.withCommas(Math.pow(10, Math.abs(pow)));
					const batex = pow >= 0 ? powerOfTen : "\\frac{1}{" + powerOfTen + "}";
					const powerOfTenNode = {
						type: "math",
						children: parseMathString(ps, batex),
					};
					powersOfTen.push(powerOfTenNode);
				}
			}
		} else {
			// Add 0's after decimal
			numbers[0] += _.repeat(
				"0",
				Math.max(0, 1 + periodIndices[0] + power - numbers[0].length)
			);
			// Add 0's before decimal
			numbers[0] =
				_.repeat("0", Math.max(0, 1 - periodIndices[0] - power)) + numbers[0];
			periodIndices[0] = numbers[0].indexOf(".");
			periodIndices[1] = periodIndices[0] + power;
			if (power > 0) {
				numbers[1] =
					numbers[0].slice(0, periodIndices[0]) +
					numbers[0].slice(periodIndices[0] + 1, periodIndices[1] + 1) +
					"." +
					numbers[0].slice(periodIndices[1] + 1);
			} else {
				numbers[1] =
					numbers[0].slice(0, periodIndices[1]) +
					"." +
					numbers[0].slice(periodIndices[1], periodIndices[0]) +
					numbers[0].slice(periodIndices[0] + 1);
			}
			// Replace all leading 0's with spaces
			let leadingZeroIndex;
			_.some(numbers[0], (d, i) => {
				if (d === ".") {
					leadingZeroIndex = i - 1;
					return true;
				} else if (d !== "0") {
					leadingZeroIndex = i;
					return true;
				}
			});
			numbers[0] =
				_.repeat(" ", leadingZeroIndex) + numbers[0].slice(leadingZeroIndex);
		}
		const digits = [numbers[0].split(""), numbers[1].split("")];

		return [
			{
				type: "decimalArrowDiagram",
				periodIndices,
				power,
				digits,
				lineArrows,
				children: [
					{
						type: "math",
						children: parseMathString(ps, "*"),
					},
					{
						type: "math",
						children: powersOfTen,
					},
					{
						type: "math",
						children: parseMathString(ps, mult.toString()),
					},
					{
						type: "math",
						children: parseMathString(ps, "="),
					},
				],
			},
		];
	},
	baseBlank: function(ps) {
		const base10Words = [
			"One",
			"Ten",
			"Hundred",
			"Thousand",
			"Ten thousand",
			"Hundred thousand",
			"Million",
			"Ten million",
			"Hundred million",
		];
		const base2Words = [
			"One",
			"Two",
			"Four",
			"Eight",
			"Sixteen",
			"Thirty-Two",
			"Sixty-Four",
		];
		let optArg = findOptionalArgument(ps);
		let base = 10;
		let labelTypes = {};
		while (optArg) {
			if (optArg.text === "2" || optArg.text === "10") {
				base = parseInt(optArg.text);
			} else if (_.includes(["exps", "nums", "words", "none"], optArg.text)) {
				labelTypes[optArg.text] = true;
			} else {
				ps.pushError(
					`"${optArg.text}" is not a valid option. Options must be a base (2 or 10)` +
						` or a label type (exps, nums, words, or none).`
				);
			}
			optArg = findOptionalArgument(ps);
		}
		//Default labelTypes to exponents and words:
		labelTypes = _.isEmpty(labelTypes) ? {exps: true, words: true} : labelTypes;
		if (labelTypes.nums || labelTypes.words) {
			labelTypes.diag = true;
			if (labelTypes.nums && labelTypes.words) {
				ps.pushError(
					"Both nums and words labels cannot be set; only one type of diagonal labels can be used."
				);
				labelTypes.nums = false;
			}
		}

		const baseWords = base === 10 ? base10Words : base2Words;
		const args = findArguments(ps, 1);
		const text = args[0]
			.getRest()
			.trim()
			.split(".");
		const hasDecimal = text.length > 1;
		//If the user asks for a set of blanks, create a string to represent those blanks
		if (text[0].charAt(0) === "n") {
			let num = parseInt(text[0].substring(1));
			text[0] = _.repeat("x", num);
			if (text.length > 1) {
				num = parseInt(text[1]);
				text[1] = _.repeat("x", num);
			}
		}
		let exp = text[0].length - 1;
		//Insert commas
		for (let i = exp - 2; i > 0; i -= 3) {
			text[0] = text[0].slice(0, i) + "," + text[0].slice(i);
		}
		const argsArr = text.join(".").split("");
		//array holding a list of math nodes with exponents
		const expArr = [];
		//array holding a list of math or text nodes with the long form of numbers
		const numArr = [];
		let pow = Math.pow(base, exp);
		_.each(argsArr, elem => {
			//We still need to push something to keep the digits aligned with their powers.
			if (elem === "," || elem === ".") {
				expArr.push({type: "math", children: []});
				numArr.push({type: "math", children: []});
			} else {
				if (labelTypes.exps) {
					expArr.push({
						type: "math",
						children: parseNonmathString(ps, "$" + base + "^{" + exp + "}$"),
					});
				}
				if (labelTypes.words) {
					numArr.push({
						type: "text",
						text: baseWords[Math.abs(exp)] + (exp < 0 ? "ths" : "s"),
					});
				}
				if (labelTypes.nums) {
					if (exp > 0) {
						numArr.push({
							type: "math",
							children: parseNonmathString(ps, "$" + pow + "$'s"),
						});
						pow /= base;
					} else if (exp === 0) {
						numArr.push({
							type: "math",
							children: parseNonmathString(ps, "$" + pow + "$'s"),
						});
						pow *= base;
					} else {
						numArr.push({
							type: "math",
							children: parseNonmathString(
								ps,
								"$\\frac1{" + NumberUtil.withCommas(pow) + "}$'s"
							),
						});
						pow *= base;
					}
				}
				exp--;
			}
		});
		//helper function for converting a character to the relevant type of node
		const toNode = function(elem) {
			if (elem === "x") {
				return {
					type: "blank",
					blankType: "simple",
					answerSize: 1,
					children: [],
				};
			} else {
				return {type: "text", text: elem};
			}
		};
		return [
			{
				type: "baseBlank",
				labelTypes,
				hasDecimal,
				children: [
					{type: "list", listData: _.map(text.join(".").split(""), toNode)},
					{type: "list", listData: expArr},
					{type: "list", listData: numArr},
				],
			},
		];
	},
	stack: function(ps) {
		const children = [];
		const optArgs = [];
		let opt = findOptionalArgument(ps);
		while (opt) {
			optArgs.push(opt.text.toLowerCase().replace(/\s+/g, ""));
			opt = findOptionalArgument(ps);
		}

		const options = {};
		const noCommaRows = [];
		const noCommaRowsRegExp = /^nocommarows\{(.+)\}$/;
		const twoDigitBlankRows = [];
		const twoDigitBlankRowsRegExp = /^twodigitblankrows\{(.+)\}$/;
		const decimalPlacesRegExp = /^decimalplaces\{(\d+)\}$/;

		const checkOptArgWithArgs = (arg, regexp, commandName, rowsArray) => {
			const match = arg.match(regexp);
			if (!/^(\d+,)*\d+$/.test(match[1])) {
				ps.pushError(
					"Check the format of the " +
						commandName +
						" argument. It should be a comma-separated list" +
						"of row numbers. (The top row is row 0). Ex: " +
						commandName +
						"{0,1}"
				);
			} else {
				match[1].split(",").forEach(r => rowsArray.push(parseInt(r)));
				options[commandName] = rowsArray;
			}
		};

		optArgs.forEach(optArg => {
			if (optArg === "big") {
				options.big = true;
			} else if (noCommaRowsRegExp.test(optArg)) {
				checkOptArgWithArgs(
					optArg,
					noCommaRowsRegExp,
					"noCommaRows",
					noCommaRows
				);
			} else if (twoDigitBlankRowsRegExp.test(optArg)) {
				checkOptArgWithArgs(
					optArg,
					twoDigitBlankRowsRegExp,
					"twoDigitBlankRows",
					twoDigitBlankRows
				);
			} else if (decimalPlacesRegExp.test(optArg)) {
				const match = optArg.match(decimalPlacesRegExp);
				const parsed = parseInt(match[1], 10);
				if (parsed > 0) {
					options.decimalPlaces = parsed;
				}
			} else {
				options.color = optArg;
			}
		});
		if (optArgs.length > _.size(options)) {
			ps.pushError(
				`Unknown optional argument encountered. Acceptable values: "noCommaRows", "big", "twoDigitBlankRows", "decimalPlaces", and/or a single color name.`
			);
		}

		const args = findArguments(ps, 2);
		if (args.length > 2)
			ps.pushError(
				`Only two required arguments are accepted. Enter the oprator and rows per the BaTeX guide.`
			);
		args.forEach(arg => (arg.text = arg.text.replace(/\s+/g, "")));
		if (!/^[\+\-]$/.test(args[0].text))
			ps.pushError(`The first argument must be the operator, "+" or "-".`);
		const rows = _.filter(
			splitArgIntoText(args[1]),
			rowPS => rowPS.text !== ""
		);

		rows.forEach((row, i) => {
			const cells = row.text.split(",");
			let node;
			const rowData = _.map(cells, cell => {
				// blank
				if (cell === "bl") {
					node = {
						type: "blank",
						blankType: "simple",
						answerSize: _.includes(twoDigitBlankRows, i) ? 2 : 1,
						initialValue: "",
						children: [],
					};
				} else if (cell === "n") {
					// nonexistent / placeholder
					node = {
						type: "text",
						text: "",
					};
				} else if (cell.includes("f")) {
					const val = _.filter(cell.split(""), c => /\d/.test(c)).join("");
					const shaded = cell.includes("s");
					node = {
						type: "fakeblank",
						answerSize: _.includes(twoDigitBlankRows, i) ? 2 : 1,
						initialValue: val,
						shaded,
						children: [],
					};
				} else {
					node = {
						type: "text",
						text: _.filter(cell.split(""), c => /\d/.test(c)).join(""),
					};
				}

				if (cell.includes("x")) {
					// cancel
					node.cancel = true;
				}
				if (_.includes(options.noCommaRows, i) && !cell.includes("f")) {
					const oldNode = node;
					node = {
						type: "italic",
						children: [oldNode],
					};
				}
				if (cell.includes("b") && cell !== "bl") {
					// bold
					const oldNode = node;
					node = {
						type: "bold",
						children: [oldNode],
					};
				}
				if (cell.includes("c")) {
					// color
					const oldNode = node;
					node = {
						type: "color",
						colorName: options.color,
						children: [oldNode],
					};
				}

				return node;
			});
			children.push({type: "stackRow", children: rowData});
		});

		return [
			_.extend(
				{
					type: "stack",
					operator: args[0].text,
					children,
				},
				options
			),
		];
	},
	divbracket: function(ps) {
		return makeMathCommandNode("longDivisionBracket", findArguments(ps, 1));
	},
	bigDecimal: function(ps) {
		const optColor = findOptionalArgument(ps);
		return [
			{
				type: "bigDecimal",
				color: optColor ? optColor.getRest() : null,
				children: [],
			},
		];
	},
	angle: function(ps) {
		return [
			{
				type: "angle",
				children: [],
			},
		];
	},
	leftarrow: function() {
		return [
			{
				type: "arrow",
				direction: "left",
				children: [],
			},
		];
	},
	rightarrow: function() {
		return [
			{
				type: "arrow",
				direction: "right",
				children: [],
			},
		];
	},
	uparrow: function() {
		return [
			{
				type: "arrow",
				direction: "up",
				children: [],
			},
		];
	},
	downarrow: function() {
		return [
			{
				type: "arrow",
				direction: "down",
				children: [],
			},
		];
	},
	botarrow: function(ps) {
		const args = findArguments(ps, 1);
		return [
			{
				type: "botarrow",
				label: args[0].text,
				children: [],
			},
		];
	},
	linearrow: function(ps) {
		const args = findArguments(ps, 1);
		const dir = args[0].getRest();
		if (!_.includes(["u", "r", "d", "l"], dir)) {
			ps.pushError("Direction must be one of u, r, d, l");
			return false;
		}
		return [
			{
				type: "linearrow",
				direction: dir,
				children: [],
			},
		];
	},
	mequal: function() {
		return [
			{
				type: "mequal",
				children: [],
			},
		];
	},
	ldots: function() {
		return [
			{
				type: "dots",
				dotStyle: "l",
				children: [],
			},
		];
	},
	cdots: function() {
		return [
			{
				type: "dots",
				dotStyle: "c",
				children: [],
			},
		];
	},
	cdot: function() {
		return [
			{
				type: "operator",
				text: "\\cdot",
				isUnary: false,
			},
		];
	},
	vdots: function() {
		return [
			{
				type: "vdots",
				children: [],
			},
		];
	},
	triangle: function(ps) {
		return [
			{
				type: "triangle",
				children: [],
			},
		];
	},
	modibot: function(ps) {
		const args = findArguments(ps, 1);

		const prependSign = x => {
			const strNum = parseInt(x, 10);
			if (isNaN(strNum) === false) {
				if (strNum < 0) {
					return "\\minus" + Math.abs(strNum);
				} else {
					return "\\plus" + strNum;
				}
			}
			return "\\text{" + x + "}";
		};

		const nodeResults = parseMathString(
			ps,
			prependSign(args[0].getRest().trim())
		);
		return [
			{
				type: "modibot",
				children: [
					{
						type: "math",
						children: nodeResults,
					},
				],
			},
		];
	},
	digitcontainer: function(ps) {
		const args = findArguments(ps, 1);
		const convertArgs = PS.convert(args[0]);
		const text = convertArgs.getRest();
		const digits = text ? text.trim().split("") : [];
		const children = digits.map(d => {
			if (d === "#") {
				return {
					type: "blank",
					blankType: "simple",
					answerSize: 1,
					children: [],
				};
			} else {
				return {type: "text", text: d};
			}
		});
		return [
			{
				type: "digitcontainer",
				children,
			},
		];
	},
	boxdigits: function(ps) {
		const optArg = findOptionalArgument(ps);
		const args = findArguments(ps, 1);
		const convertArgs = PS.convert(args[0]);
		const text = convertArgs.getRest();
		const digits = text ? text.split(";").map(a => a.trim()) : [];
		return [
			{
				type: "boxdigits",
				digits,
				noOutline: optArg && optArg.getRest() === "noOutline",
				children: [],
			},
		];
	},
	carry: function(ps) {
		return makeMathCommandNode("carry", findArguments(ps, 2));
	},
	cancel: function(ps) {
		return makeMathCommandNode("cancel", findArguments(ps, 1), {
			cancelType: "basic",
		});
	},
	xcancel: function(ps) {
		return makeMathCommandNode("cancel", findArguments(ps, 1), {
			cancelType: "x",
		});
	},
	lcancel: function(ps) {
		const optArg = findOptionalArgument(ps);
		const optArgExtra = findOptionalArgument(ps);

		const arg1 = optArg && optArg.getRest();
		const arg2 = optArgExtra && optArgExtra.getRest();

		let cancelType = "lt"; //default "backslash-top"
		let center = false;
		let noShrink = false;

		if (_.includes(arg1, "c") || _.includes(arg2, "c")) {
			center = true;
		}
		if (_.includes(arg1, "s") || _.includes(arg2, "s")) {
			noShrink = true;
		}

		if (_.includes(["rb", "rt", "lb", "lt"], arg1)) {
			cancelType = arg1;
		} else if (_.includes(["rb", "rt", "lb", "lt"], arg2)) {
			cancelType = arg2;
		} else if ((arg1 || arg2) && !center && !noShrink) {
			ps.pushError("Options not one of rb, rt, lb, lt, c, or s.");
			return false;
		}

		return makeMathCommandNode("cancel", findArguments(ps, 2), {
			cancelType,
			center,
			noShrink,
		});
	},
	blank: function(ps) {
		const optArgsArray = findOptionalArguments(ps);

		let vPadding = 0;
		let outlineColor = "";
		let initialValue = false;
		let shaded;
		_.each(optArgsArray, optArg => {
			if (!optArg) return;
			if (optArg.match(/^vPadding=[1-9]\d*$/)) {
				vPadding = parseInt(optArg.split("=")[1]);
			} else if (optArg.match(/^outlineColor=\w*$/)) {
				outlineColor = optArg.split("=")[1];
			} else if (optArg.match(/^shaded$/)) {
				shaded = true;
			} else {
				initialValue = optArg;
			}
		});

		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}

		return [
			{
				type: "blank",
				blankType: "simple",
				answerSize: parseInt(args[0].getRest()) || 1,
				initialValue,
				children: [],
				vPadding,
				outlineColor,
				shaded,
			},
		];
	},
	fakeblank: function(ps) {
		const optArg = findOptionalArgument(ps);
		const optStylingArg = findOptionalArgument(ps);

		const initialValue = optArg ? optArg.getRest() : false;
		const stylingArgSplit = optStylingArg
			? splitArgIntoText(optStylingArg.getRest())
			: [];

		let shaded = false;
		let outlineColor = "";
		let color = "";
		let bgColor = "";
		_.forEach(stylingArgSplit, function(arg) {
			if (arg.text === "shaded") {
				shaded = true;
			} else if (_.startsWith(arg.text, "outlineColor=")) {
				outlineColor = _.replace(arg.text, "outlineColor=", "");
			} else if (_.startsWith(arg.text, "bgColor=")) {
				bgColor = _.replace(arg.text, "bgColor=", "");
			} else {
				color = arg.text;
			}
		});

		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return [
			{
				type: "fakeblank",
				answerSize: args[0].getRest(),
				color,
				outlineColor,
				initialValue,
				children: [],
				shaded,
				bgColor,
			},
		];
	},
	varbox: function(ps) {
		const optArg = findOptionalArgument(ps);
		const initialValue = optArg ? optArg.getRest() : false;
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		return [
			{
				type: "varbox",
				answerSize: args[0].getRest(),
				initialValue,
				colorName: args[1]
					.getRest()
					.trim()
					.toLowerCase(),
				children: [],
			},
		];
	},
	fracblank: function(ps) {
		const optArg = findOptionalArgument(ps);
		const initialValue = optArg ? optArg.getRest() : false;
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return [
			{
				type: "blank",
				blankType: "fraction",
				answerSize: args[0].getRest(),
				initialValue: initialValue,
				children: [],
			},
		];
	},
	mixblank: function(ps) {
		const optArg = findOptionalArgument(ps);
		const initialValue = optArg ? optArg.getRest() : false;
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		const wholeSize = parseInt(args[0].getRest());
		const fracSize = parseInt(args[1].getRest());
		if (!wholeSize || !fracSize) {
			ps.pushError("The blank sizes must be positive integers.");
			return false;
		}
		return [
			{
				type: "blank",
				blankType: "mixed",
				wholeSize: wholeSize,
				fracSize: fracSize,
				initialValue: initialValue,
				children: [],
			},
		];
	},
	image: function(ps) {
		const optArg = findOptionalArgument(ps);
		const readAloudText = (optArg && optArg.getRest()) || "";
		const args = findArguments(ps, 1, {requireBrace: true});
		if (!args) {
			return false;
		}
		return parseImage(args[0], readAloudText);
	},
	clock: function(ps) {
		const optArgsText = findOptionalArguments(ps).map(optArg =>
			optArg ? optArg.trim() : optArg
		);
		const forPrint = optArgsText.includes("print");
		const minuteOnly = optArgsText.includes("minuteOnly");
		const hourOnly = optArgsText.includes("hourOnly");

		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return [
			{
				type: "clock",
				time: args[0].getRest(),
				forPrint,
				minuteOnly,
				hourOnly,
			},
		];
	},
	balancescale: function(ps) {
		const optArg = findOptionalArgument(ps);
		const optText = ((optArg && optArg.getRest()) || "").toLowerCase();
		let sizeModifier = optText;
		if (optText && !_.includes(["small", "large"], optText)) {
			ps.pushError(
				optText +
					" is not a valid size modifier for the balance scale. Only small and large are supported."
			);
			sizeModifier = null;
		}
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		const critterNames = _.keys(RenderHelpers.critterInfo);
		const parseWeight = w =>
			_.includes(critterNames, w.getRest())
				? {isCritter: true, name: w.getRest()}
				: makeMathArgNode(w);
		const leftWeights = args[0]
			? splitArgIntoText(args[0]).map(parseWeight)
			: [];
		const rightWeights = args[1]
			? splitArgIntoText(args[1]).map(parseWeight)
			: [];
		return [
			{
				type: "balanceScale",
				leftWeights: leftWeights,
				rightWeights: rightWeights,
				sizeModifier: sizeModifier,
			},
		];
	},
	coins: function(ps) {
		const args = findArguments(ps, 3);
		if (!args) {
			return false;
		}
		const imgType = args[0].getRest();
		const position = parseInt(args[1].getRest());
		const num = parseInt(args[2].getRest());
		if (_.indexOf(["H", "h", "T", "t", "s", "f", "b"], imgType) === -1) {
			ps.pushError(
				imgType +
					" is not a valid image type. You must input H, h, T, t, s, f, or b"
			);
			return false;
		}
		if (!_.includes(_.range(1, 4), position)) {
			ps.pushError(
				position +
					" is not a valid position. You must provide a number from 1-3."
			);
			return false;
		}
		return [
			{
				type: "coins",
				imgType,
				position,
				num,
			},
		];
	},
	blocks: function(ps) {
		const optArg = findOptionalArgument(ps);
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		const imgType = parseInt(args[0].getRest());
		const num = parseInt(args[1].getRest());
		if (_.indexOf([1, 5, 10, 100], imgType) === -1) {
			ps.pushError(
				imgType + " is not a valid image type. You must input 1, 5, 10, or 100"
			);
			return false;
		}
		const color = optArg && optArg.getRest();
		if (
			color &&
			!["blue", "orange", "green", "skyblue", "yellow"].includes(color)
		) {
			ps.pushError(
				color + " is not a valid color. You must input blue, orange, or green"
			);
			return false;
		}
		return [
			{
				type: "blocks",
				imgType,
				num,
				color,
			},
		];
	},
	btenblocks: function(ps) {
		const optArg = findOptionalArgument(ps);
		const args = findSomeArguments(ps);
		const numbers = _.map(args, splitArgIntoNumbers);
		if (!args || numbers.length === 0) {
			return false;
		}
		const imgTypes = _.map(numbers, arg => arg[0]);
		const nums = _.map(numbers, arg => parseInt(arg[1]));

		// check each image to make sure it's valid
		for (let i = 0; i < imgTypes.length; i++) {
			if (_.indexOf([1, 5, 10, 100], imgTypes[i]) === -1) {
				ps.pushError(
					imgTypes[i] +
						" is not a valid image type. You must input 1, 5, 10, or 100"
				);
				return false;
			}
		}

		// split up the 100 blocks into groups of 5 to make them display side by side
		for (let i = 0; i < imgTypes.length; i++) {
			if (imgTypes[i] === 100) {
				let num100s = nums[i];
				while (num100s > 5) {
					imgTypes.push(100);
					nums.push(5);
					num100s -= 5;
				}
				nums[i] = num100s;
			}
		}

		// sort the image list from largest to smallest
		let numSwapped, temp;
		do {
			numSwapped = 0;
			for (let i = 1; i < imgTypes.length; i++) {
				if (
					imgTypes[i - 1] < imgTypes[i] ||
					(imgTypes[i - 1] === imgTypes[i] && nums[i - 1] < nums[i])
				) {
					// swap values for images
					temp = imgTypes[i];
					imgTypes[i] = imgTypes[i - 1];
					imgTypes[i - 1] = temp;

					// swap same values for numbers
					temp = nums[i];
					nums[i] = nums[i - 1];
					nums[i - 1] = temp;

					numSwapped++;
				}
			}
		} while (numSwapped > 0);

		// Check the color to make sure it's valid (light blue is another name for skyblue)
		const color = optArg && optArg.getRest();
		if (
			color &&
			!["blue", "orange", "green", "skyblue", "yellow"].includes(color)
		) {
			ps.pushError(
				color +
					" is not a valid color. You must input blue, orange, skyblue/light blue, or green"
			);
			return false;
		}

		return _.map(imgTypes, (imgType, i) => ({
			type: "btenblocks",
			imgType,
			num: nums[i],
			color,
		}));
	},
	measuringcup: function(ps) {
		const optArg = findOptionalArgument(ps);
		const secondOptArg = findOptionalArgument(ps);
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const fillLevel = parseInt(args[0].getRest()) || 0;
		const fillStyle = optArg && optArg.getRest();
		const restFillStyle = secondOptArg && secondOptArg.getRest();
		return [
			{
				type: "measuringCup",
				fillLevel: fillLevel,
				fillStyle: fillStyle,
				restFillStyle: restFillStyle,
			},
		];
	},
	graduatedcylinder: function(ps) {
		const optArg = findOptionalArgument(ps);
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const fillLevel = parseInt(args[0].getRest()) || 0;
		const fillStyle = optArg && optArg.getRest();
		return [
			{
				type: "graduatedCylinder",
				fillLevel: fillLevel,
				fillStyle: fillStyle,
			},
		];
	},
	thermometer: function(ps) {
		const optArg = findOptionalArgument(ps);
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const fillFaren = parseInt(args[0].getRest());
		const fillStyle = optArg && optArg.getRest();
		return [
			{
				type: "thermometer",
				fillFaren,
				fillStyle,
			},
		];
	},
	mcnugget: function(ps) {
		const optArg = findOptionalArgument(ps);
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return [
			{
				type: "mcnugget",
				chickens: splitArgIntoNumbers(args[0]),
				shaded: optArg && optArg.getRest() === "shaded",
				children: [],
			},
		];
	},
	colorPositionText: function(ps) {
		const args = findArguments(ps, 3);
		if (!args) {
			return false;
		}
		const toNode = x => ({type: "text", text: x});
		const nodeResults = _.map(
			_.flatten(parseMathString(ps, args[0].text)),
			x => {
				return x.type === "text" ? _.map(x.text.split(""), e => toNode(e)) : x;
			}
		);
		const indices = _.map(args[1].text.split(","), e => parseInt(e));
		return [
			{
				type: "colorPositionText",
				color: args[2].text,
				indices: indices,
				children: _.flatten(nodeResults),
			},
		];
	},
	cdice: function(ps) {
		let optArg = findOptionalArgument(ps);
		// sets defaults for optional arguments
		let fillColor = "purple";
		let dotColor = "black";
		let diceAngle = 0;
		while (optArg) {
			let text = null;
			if (_.startsWith(optArg.text, "color=") && optArg.text !== "") {
				fillColor = Helpers.parseColor(ps, optArg.text);
			} else if (_.startsWith(optArg.text, "dotcolor=")) {
				text = optArg.text.substring(9);
				if (text.length > 0) {
					dotColor = text;
				}
			} else if (_.startsWith(optArg.text, "angle=")) {
				text = optArg.text.substring(6);
				if (text.length > 0) {
					diceAngle = text;
				}
			} else {
				ps.pushError(
					optArg.text +
						" is not a valid optional argument. color=COLOR_NAME, dotcolor=COLOR_NAME, angle=DEGREES are the only valid options."
				);
				return false;
			}
			optArg = findOptionalArgument(ps);
		}
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const diceValue = parseInt(args[0].getRest());
		if (!_.includes(_.range(1, 7), diceValue)) {
			// check for dice range
			ps.pushError(
				args[0].getRest() +
					" is not a valid dice number. You must provide a number from 1-6."
			);
			return false;
		}
		return [
			{
				type: "coloredDice",
				fillColor,
				dotColor,
				diceAngle,
				diceValue,
			},
		];
	},
	textShape: function(ps) {
		let optArg = findOptionalArgument(ps);
		let fillColor = null;
		while (optArg) {
			if (_.startsWith(optArg.text, "color=")) {
				fillColor = Helpers.parseColor(ps, optArg.text);
			} else {
				ps.pushError(
					optArg.text +
						" is not a valid optional argument. color=COLOR_NAME is the only valid options."
				);
				return false;
			}
			optArg = findOptionalArgument(ps);
		}
		const args = findArguments(ps, 2);
		const varg0 = !args ? "" : args[0].getRest();
		const shapes = {
			s: "square",
			c: "circle",
			t: "triangle",
		};
		if (!_.includes(Object.keys(shapes), varg0)) {
			ps.pushError("Please specify a valid symbol.");
			return false;
		}

		const shape = shapes[varg0];
		const mathNodeChildContent = args[1].getRest().trim() || "";
		const mathNodeChildArr = parseMathString(ps, mathNodeChildContent);
		const textShapeChild = mathNodeChildArr.length
			? [{type: "math", children: mathNodeChildArr}]
			: [];
		return [
			{
				type: "textShape",
				shape: shape,
				fillStyle: fillColor,
				children: textShapeChild,
			},
		];
	},
	sncard: function(ps) {
		let size = "medium";

		let optArg = findOptionalArgument(ps);
		let color = "black";
		while (optArg) {
			if (optArg.text === "small") {
				size = "small";
			} else if (optArg.text === "large") {
				size = "large";
			} else if (_.startsWith(optArg.text, "color=")) {
				color = Helpers.parseColor(ps, optArg.text);
			} else {
				ps.pushError(
					optArg.text +
						" is not a valid optional argument. small, large, and color=COLOR_NAME are the only valid options."
				);
				return false;
			}
			optArg = findOptionalArgument(ps);
		}

		const args = findArguments(ps, 1);
		const text = !args ? "" : args[0].getRest();
		const shapes = {
			sq: "square",
			ci: "circle",
			tr: "triangle",
			st: "★",
			cl: "♣",
			dm: "♦",
			ht: "♥",
			sp: "♠",
		};
		let content = text;
		let isSymbol = false;
		if (text.length > 1) {
			if (!_.includes(Object.keys(shapes), text)) {
				ps.pushError("Please specify one character or a valid symbol.");
				return false;
			}
			content = shapes[text];
			isSymbol = true;
		}
		return [
			{
				type: "shapeNumberCard",
				text: content,
				isSymbol,
				size: size,
				color: color,
				children: [],
			},
		];
	},
	card: function(ps) {
		let optArg = findOptionalArgument(ps);
		// set defaults
		let color = "black";
		let customColor = false;
		let outline = true;
		while (optArg) {
			if (_.startsWith(optArg.text, "color=")) {
				color = Helpers.parseColor(ps, optArg.text);
				customColor = true;
			} else if (optArg.text === "outline=false") {
				// option for card outline
				outline = false;
			} else {
				ps.pushError(
					optArg.text +
						" is not a valid optional argument. small, large, and color=COLOR_NAME are the only valid options."
				);
				return false;
			}
			optArg = findOptionalArgument(ps);
		}

		const args = findArguments(ps, 2);
		const text = !args ? "" : args[0].getRest();
		const shapes = {
			cl: "♣",
			dm: "♦",
			ht: "♥",
			sp: "♠",
		};
		let content = text;
		if (text.length > 1) {
			if (!_.includes(Object.keys(shapes), text)) {
				ps.pushError("Please specify one character or a valid symbol.");
				return false;
			}
			content = shapes[text];
			if (content === "♦" || content === "♥") {
				if (!customColor) {
					color = "red";
				}
			}
		}
		if (!args) {
			return false;
		}
		const cardValue = parseInt(args[1].getRest());
		if (!_.includes(_.range(2, 11), cardValue)) {
			// check for card range
			ps.pushError(
				args[0].getRest() +
					" is not a valid card number. You must provide a number from 2-10."
			);
			return false;
		}
		return [
			{
				type: "card",
				text: content,
				color: color,
				outline: outline,
				cardValue: cardValue,
				children: [],
			},
		];
	},
	inputCircleButton: function(ps) {
		let selected = false;
		const selectedArg = findOptionalArgument(ps);
		if (selectedArg && selectedArg.text !== "selected") {
			ps.pushError(
				selectedArg.text +
					" is not a valid optional argument. [selected] is the only valid option."
			);
			return false;
		} else if (selectedArg && selectedArg.text === "selected") {
			selected = true;
		}
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const type = args[0].getRest().toLowerCase();
		const types = ["simple", "fraction", "mixed", "overbar", "sqrt"];
		if (!_.includes(types, type)) {
			ps.pushError(
				type +
					" is not a valid button type. Possible types are: " +
					types.join(", ") +
					"."
			);
			return false;
		}
		return [
			{
				type: "inputCircleButton",
				inputType: type,
				selected,
			},
		];
	},
	zigzag: function(ps) {
		let size = findOptionalArgument(ps);
		size = size ? parseFloat(size.text) : 1;
		if (!_.isFinite(size)) {
			ps.pushError("The size optional argument must be a number like [0.8].");
			size = 1;
		}
		const args = findArguments(ps, 1);
		const text = !args ? "" : args[0].getRest();
		const nums = text ? text.split(";").map(a => a.trim()) : [];
		if (nums.length % 2 === 0) {
			ps.pushError("There must be an odd number of numbers.");
			return false;
		}
		const numbers = [];
		nums.forEach(n => {
			if (n.charAt(0) === "#") {
				numbers.push({
					type: "blank",
					blankType: "simple",
					answerSize: parseInt(n.substring(1)) || 2,
					children: [],
				});
			} else {
				numbers.push({
					type: "math",
					children: parseMathString(ps, NumberUtil.withCommas(n)),
				});
			}
		});
		return [
			{
				type: "zigzagPuzzle",
				size,
				children: [
					{
						type: "math",
						children: numbers,
					},
				],
			},
		];
	},
	hchartgrid: function(ps) {
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const text = args[0].getRest();
		if (!text) {
			ps.pushError("You must specify at least one value from 2-12.");
			return false;
		}
		const multiples = text
			.split(";")
			.filter(t => !!t)
			.map(x => parseInt(x));
		_.forEach(multiples, multiple => {
			if (_.isNaN(multiple) || multiple < 2 || multiple > 12) {
				ps.pushError(
					`Every semicolon separated multiple must be a number from 2 to 12. ${multiple} is not.`
				);
				return false;
			}
		});
		return [
			{
				type: "hundredsChartGrid",
				multiples,
				children: [],
			},
		];
	},
	hchartsection: function(ps) {
		const DEFAULT_EVEN = 4;
		const DEFAULT_ODD = 5;
		const options = {
			gridSize: 10, // 10x10 grid
			padColor: "white",
		};
		const regex = {
			width: /^w(idth)?\s*=\s*(\d+)$/i,
			height: /^h(eight)?\s*=\s*(\d+)$/i,
			blanks: /^blanks\s*=\s*([\s\d;]+)$/i,
			padColor: /^(c|padColor)\s*=\s*([a-zA-Z]+)$/i,
		};
		const edgesRegex = {
			top: /^t(op)?$/i,
			bottom: /^b(ottom)?$/i,
			left: /^l(eft)?$/i,
			right: /^r(ight)?$/i,
		};

		let optArg,
			matches,
			children = [];
		// eslint-disable-next-line no-cond-assign
		while ((optArg = findOptionalArgument(ps))) {
			const text = optArg.text;
			// eslint-disable-next-line no-cond-assign
			if ((matches = regex.width.exec(text)))
				options.width = parseInt(matches[2]);
			// eslint-disable-next-line no-cond-assign
			else if ((matches = regex.height.exec(text)))
				options.height = parseInt(matches[2]);
			// eslint-disable-next-line no-cond-assign
			else if ((matches = regex.blanks.exec(text))) {
				// validate before saving
				const blanks = matches[1]
					.split(";")
					.filter(b => !!b)
					.map(b => parseInt(b));
				const invalids = blanks.filter(b => _.isNaN(b) || b < 1 || b > 100);
				// save without invalid values
				options.blanks = _.sortBy(blanks.filter(b => !invalids.includes(b)));
				if (invalids.length) {
					ps.pushError(
						`Every number listed as a missing value must be from 1 to 100 (inclusive). ${invalids.join(
							", "
						)} ${
							invalids.length === 1 ? "is" : "are"
						} not, so it will be skipped.`
					);
				}
				// make children nodes for blanks
				children = _.map(options.blanks, blank => {
					return {
						type: "blank",
						blankType: "simple",
						answerSize: blank.toString().length,
						children: [],
					};
				});
				// eslint-disable-next-line no-cond-assign
			} else if ((matches = regex.padColor.exec(text))) {
				// check that this color is available before saving
				const padColor = matches[2];
				const colors = ColorUtil.getDiagramColors();
				if (padColor in colors) {
					options.padColor = padColor;
				} else {
					ps.pushError(`Specified pad color "${padColor}" is not supported.`);
				}
			} else {
				let edgeName = null;
				for (const edge in edgesRegex) {
					// eslint-disable-next-line no-cond-assign
					if ((matches = edgesRegex[edge].exec(text))) {
						edgeName = edge;
						break;
					}
				}

				if (edgeName) {
					options[edgeName] = true;
				} else {
					ps.pushError(`Unrecognized optional argument: "${text}"`);
				}
			}
		}

		// validate optional arguments

		// I'll be using a bunch of object destructuring throughout the validation
		// code to make the conditional expressions easier to read.
		const {width, height} = options;
		let {left, right, top, bottom} = options;
		if (top && bottom) {
			ps.pushError(`Only one of "top" or "bottom" can be true.`);
			delete options.top;
			delete options.bottom;
		}
		if (left && right) {
			ps.pushError(`Only one of "left" or "right" can be true.`);
			delete options.left;
			delete options.right;
		}

		({left, right, top, bottom} = options); // reassign in case values changed
		if (!_.isNil(width)) {
			if (width < 1 || width > 10) {
				ps.pushError(
					`"Width" option must be a value between 1 and 10 (inclusive).`
				);
				delete options.width;
			} else if ((left || right) && width % 2 === 1) {
				ps.pushError(
					`If focusing on a left or right edge, "width" must be an even number.`
				);
				delete options.width;
			} else if (!(left || right) && width % 2 === 0) {
				ps.pushError(
					`If not focusing on a left or right edge, "width" must be an odd number.`
				);
				delete options.width;
			}
		}
		if (!_.isNil(height)) {
			if (height < 1 || height > 10) {
				ps.pushError(
					`"Height" option must be a value between 1 and 10 (inclusive).`
				);
				delete options.height;
			} else if ((top || bottom) && height % 2 === 1) {
				ps.pushError(
					`If focusing on a top or bottom edge, height must be an even number.`
				);
				delete options.height;
			} else if (!(top || bottom) && height % 2 === 0) {
				ps.pushError(
					`If not focusing on a top or bottom edge, height must be an odd number.`
				);
				delete options.height;
			}
		}

		const args = findArguments(ps, 1);
		if (args) {
			const text = args[0].getRest();
			if (text !== "") {
				const center = parseInt(text);
				if (_.isNaN(center)) {
					ps.pushError("Specified center value is not a valid number.");
				} else if (center < 1 || center > 100) {
					ps.pushError(
						"If you specify a center value, it must be from 1 to 100 (inclusive)."
					);
				} else {
					options.center = center;
				}
			}
		}
		/**
		 * If no regular argument, let's just assume the user wants the full grid.
		 * This technically makes an optional argument, but this will probably be
		 * the most-used argument, so let's just do it this way to save the user
		 * from having input another optional [center=xx] argument. Besides, everything
		 * revolves around the center, so this would be considered the "main"/"primary"
		 * argument either way.
		 */

		const DEFAULT_WIDTH = left || right ? DEFAULT_EVEN : DEFAULT_ODD;
		const DEFAULT_HEIGHT = top || bottom ? DEFAULT_EVEN : DEFAULT_ODD;
		if (options.center) {
			// if there's a center value, let's fallback to default width/height if necessary
			if (!options.width && !options.height) {
				// neither height nor width was set
				options.width = DEFAULT_WIDTH;
				options.height = DEFAULT_HEIGHT;
			} else if (!options.height) {
				// only width was set
				options.height = options.width;
			} else if (!options.width) {
				// only height was set
				options.width = options.height;
			}
		} else {
			// wipe width and height if no center value
			delete options.width;
			delete options.height;
		}

		return [
			{
				type: "hundredsChartSection",
				options,
				children,
			},
		];
	},
	hchartsquare: function(ps) {
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const text = args[0].getRest();
		if (!text) {
			ps.pushError("You must specify at least one value from 2-12.");
			return false;
		}
		const multiples = text
			.split(";")
			.filter(t => !!t)
			.map(x => parseInt(x));
		for (let i = 0; i < multiples.length; i++) {
			if (_.isNaN(multiples[i]) || multiples[i] < 2) {
				ps.pushError(
					"Every semicolon separated multiple must be a number from 2 to 12. " +
						multiples[i] +
						" is not."
				);
				return false;
			}
		}
		return [
			{
				type: "hundredsChartSquare",
				multiples,
				children: [],
			},
		];
	},
	arrowlist: function(ps) {
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		const listTerms = splitArgIntoMathNodes(args[0]);
		let i;
		for (i = 0; i < listTerms.length; i++) {
			//Checking to see if any of the things in the list are just an operator, if so, they should become non-unary
			if (listTerms[i].children.length === 1) {
				if (listTerms[i].children[0].type === "operator") {
					listTerms[i].children[0].isUnary = false;
				}
			}
		}
		let arrowInfo;
		try {
			arrowInfo = JSON.parse("[" + args[1].getRest() + "]");
		} catch (error) {
			ps.pushError(
				'The arrow information is of the wrong form. Each arrow should be in object form like {start: 1, end: 4, bel: "true"} and the arrow list should be comma separated.'
			);
			return false;
		}
		for (i = 0; i < arrowInfo.length; i++) {
			if (arrowInfo[i].hasOwnProperty("label")) {
				arrowInfo[i].labelNode = makeMathArgNode(
					ps.withText(arrowInfo[i].label + "")
				);
			}
			if (!_.isNumber(arrowInfo[i].start) || !_.isNumber(arrowInfo[i].end)) {
				ps.pushError(
					"Every arrow's information must include a start and end numerical value. Arrow " +
						(i + 1) +
						" is missing at least one of those things"
				);
				return false;
			} else if (
				arrowInfo[i].start <= 0 ||
				arrowInfo[i].end <= 0 ||
				arrowInfo[i].start > listTerms.length ||
				arrowInfo[i].end > listTerms.length
			) {
				ps.pushError(
					"Every arrow's start and end value must be between 1 and the number of terms (" +
						listTerms.length +
						"). Arrow " +
						(i + 1) +
						" is incorrect."
				);
				return false;
			}
		}
		return [
			{
				type: "arrowList",
				arrowInfo: arrowInfo,
				children: [
					{
						type: "math",
						children: listTerms,
					},
				],
			},
		];
	},
	placevalueconnection: function(ps) {
		const args = findArguments(ps, 4);
		if (!args) {
			return false;
		}

		const constructUnderlabel = (digit, label) => {
			// puts a label under a number
			return `\\underlabel{${digit}}{${label}}`;
		};
		const makeUnderlabeledNumber = num => {
			// takes a 1/2 digit numbers and labels its place values
			const one = num % 10;
			const ten = (num - one) / 10;

			const rtn = [];
			if (ten !== 0) {
				rtn.push(constructUnderlabel(ten, "\\text{tens}"));
			}
			rtn.push(constructUnderlabel(one, "\\text{ones}"));
			return rtn;
		};

		// calculating answer to figure out blank size
		const blankPart = [];
		if (args[3].text === "blank") {
			const answer =
				args[1].text === "+"
					? parseInt(args[0].text) + parseInt(args[2].text)
					: parseInt(args[0].text) - parseInt(args[2].text);
			if (answer >= 10) {
				blankPart.push(constructUnderlabel("\\blank{1}", "\\text{tens}"));
			}
			blankPart.push(constructUnderlabel("\\blank{1}", "\\text{ones}"));
		}

		const delimitedEq = [args[0].text + args[1].text + args[2].text + "="];

		// numberIndexes[i] are the indexes of delimedEq of the ith number
		// This is used for arrow creation with \arrowlist
		const numberIndexes = [];

		// Number before the +/-
		const firstNum = makeUnderlabeledNumber(args[0].text);
		numberIndexes.push(
			_.range(delimitedEq.length, firstNum.length + delimitedEq.length)
		);
		delimitedEq.push(...firstNum);

		// The +/-
		delimitedEq.push(args[1].text);

		// Number after the +/-
		const secondNum = makeUnderlabeledNumber(args[2].text);
		numberIndexes.push(
			_.range(delimitedEq.length, secondNum.length + delimitedEq.length)
		);
		delimitedEq.push(...secondNum);

		// Number that is seen as blanks
		if (blankPart.length) {
			delimitedEq.push("=");

			numberIndexes.push(
				_.range(delimitedEq.length, blankPart.length + delimitedEq.length)
			);
			delimitedEq.push(...blankPart);
		}

		const oneLink = {below: true};
		const tenLink = {below: false};

		const addNextBounce = (link, index) => {
			// \arrowlist indexes from 1
			index += 1;

			if (!link.start) {
				link.start = index;
			} else if (!link.end) {
				link.end = index;
			} else if (!link.bounce) {
				link.bounce = link.end;
				link.end = index;
			} else {
				// Only supports one bounce
			}
		};

		for (let i = 0; i < numberIndexes.length; i++) {
			if (numberIndexes[i].length === 2) {
				addNextBounce(tenLink, _.first(numberIndexes[i]));
			}
			addNextBounce(oneLink, _.last(numberIndexes[i]));
		}

		let linkList = JSON.stringify(oneLink);
		if (tenLink.end) {
			linkList += "," + JSON.stringify(tenLink);
		}

		return parseMathString(
			ps,
			`\\arrowlist{${delimitedEq.join(";")}}{${linkList}}`
		);
	},
	dayseq: function(ps) {
		const args = findArguments(ps, 3);
		if (!args) {
			return false;
		}
		const days = args[1]
			.getRest()
			.split(";")
			.map(a => ({
				type: "nonmath",
				children: makeFormattedText("italic", ps.withText(a)),
			}));
		const topLabels = splitArgIntoNonmathNodes(args[2]);
		if (topLabels.length && topLabels.length !== days.length) {
			ps.pushError(
				"If the day sequence has labels, there must be as many labels as days separated by semicolons."
			);
			return;
		}

		return [
			{
				type: "dayArrowSequence",
				children: [
					{
						type: "math",
						children: parseMath(args[0]),
					},
					{
						type: "list",
						children: days,
					},
					{
						type: "list",
						children: topLabels,
					},
				],
			},
		];
	},
	eqarrows: function(ps) {
		const directionInfo = findOptionalArgument(ps);
		const direction =
			(directionInfo &&
				directionInfo.text &&
				directionInfo.text.toLowerCase()) ||
			"right";
		const args = findArguments(ps, 3);
		if (!args) {
			return false;
		}
		const equalArrowsNode = makeMathCommandNode("equalArrows", args);
		equalArrowsNode[0].direction = direction;
		equalArrowsNode[0].children.push({type: "text", text: "  =  "});
		return equalArrowsNode;
	},
	distarrows: function(ps) {
		const opt1 = findOptionalArgument(ps);
		const opt2 = findOptionalArgument(ps);
		let direction = "right";
		let sourceNum;
		const regex = /^[01]$/;
		if (opt1 && opt1.text && opt1.text.toLowerCase() === "left") {
			direction = opt1.text.toLowerCase();
		} else if (opt2 && opt2.text && opt2.text.toLowerCase() === "left") {
			direction = opt2.text.toLowerCase();
		}
		if (opt2 && opt2.text && regex.test(opt2.text)) {
			sourceNum = opt2.text;
		} else if (opt1 && opt1.text && regex.test(opt1.text)) {
			sourceNum = opt1.text;
		}
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		const factorMath = splitArgIntoMathNodes(args[0]);
		if (factorMath.length !== 1 && factorMath.length !== 3) {
			ps.pushError(
				"Distribution arrows require either one or three terms separated by semicolons like {20} or {20;+;7} as the first argument."
			);
			return;
		}
		const distributedMath = splitArgIntoMathNodes(args[1]);
		if (distributedMath.length !== 3) {
			ps.pushError(
				"Distribution arrows require three terms to distribute between separated by semicolons like {20;+;7} as the second argument."
			);
			return;
		}
		const needsBottomArrows = factorMath.length === 3 && !sourceNum;
		_.forEach([factorMath, distributedMath], math => {
			if (math.length === 3) {
				//Make operations in the center not be unary so 6-5 doesn't become 6 then negative 5
				if (
					math[1] &&
					math[1].children.length &&
					math[1].children[0].type === "operator"
				) {
					math[1].children[0].isUnary = false;
				}
				//Add parentheses around the term
				math.unshift({type: "math", children: parseMathString(ps, "(")});
				math.push({type: "math", children: parseMathString(ps, ")")});
			}
		});

		const makeTimesSymbol = () => {
			const node = {type: "math", children: parseMathString(ps, "*")};
			node.children[0].isUnary = false;
			return node;
		};
		let arrowPositions, distChildren;
		if (direction === "right") {
			distChildren = _.concat(factorMath, makeTimesSymbol(), distributedMath);
			if (sourceNum === "0") {
				arrowPositions = [
					{start: 1, end: 7},
					{start: 1, end: 9, doubleHeight: true},
				];
			} else if (sourceNum === "1") {
				arrowPositions = [
					{start: 3, end: 7},
					{start: 3, end: 9, doubleHeight: true},
				];
			} else if (!needsBottomArrows) {
				arrowPositions = [
					{start: 0, end: 3},
					{start: 0, end: 5, doubleHeight: true},
				];
			} else {
				arrowPositions = [
					{start: 1, end: 7},
					{start: 1, end: 9, doubleHeight: true},
					{start: 3, end: 7, isBelow: true},
					{start: 3, end: 9, doubleHeight: true, isBelow: true},
				];
			}
		} else {
			distChildren = _.concat(distributedMath, makeTimesSymbol(), factorMath);
			if (sourceNum === "0") {
				arrowPositions = [
					{start: 7, end: 3},
					{start: 7, end: 1, doubleHeight: true},
				];
			} else if (sourceNum === "1") {
				arrowPositions = [
					{start: 9, end: 3},
					{start: 9, end: 1, doubleHeight: true},
				];
			} else if (!needsBottomArrows) {
				arrowPositions = [
					{start: 6, end: 3},
					{start: 6, end: 1, doubleHeight: true},
				];
			} else {
				arrowPositions = [
					{start: 9, end: 3},
					{start: 9, end: 1, doubleHeight: true},
					{start: 7, end: 3, isBelow: true},
					{start: 7, end: 1, doubleHeight: true, isBelow: true},
				];
			}
		}
		return [
			{
				type: "distributionArrows",
				arrowPositions,
				children: [
					{
						type: "math",
						children: distChildren,
					},
				],
			},
		];
	},
	longdiv: function(ps) {
		const optionalArguments = findOptionalArguments(ps);

		const noSum = optionalArguments.includes("nosum");
		const sideBar = optionalArguments.includes("sidebar");
		const noStepBreakdown = optionalArguments.includes("nostepbreakdown");
		let quotientDisplayType = "list";
		if (optionalArguments.includes("plus")) {
			quotientDisplayType = "plus";
		} else if (optionalArguments.includes("noquotient")) {
			quotientDisplayType = "none";
		}

		let nudgeNum = 0;
		_.each(optionalArguments, optArg => {
			if (optArg && optArg.match(/^nudge=[1-9]\d*$/)) {
				nudgeNum = parseInt(optArg.split("=")[1]);
			}
		});

		const args = findArguments(ps, 5);
		if (!args) {
			return false;
		}
		const addCommas = function(arg) {
			if (arg && !isNaN(Helpers.parseCommaNumber(arg.text))) {
				arg.text = Helpers.withCommas(arg.text);
			}
			return arg;
		};
		return [
			{
				type: "longDivision",
				quotientDisplayType: quotientDisplayType,
				sideBar,
				noStepBreakdown,
				noSum,
				nudgeNum,
				children: [
					// Dividend
					{
						type: "math",
						children: parseMath(addCommas(args[0])),
					},
					// Divisor
					{
						type: "math",
						children: parseMath(addCommas(args[1])),
					},
					// Partial Quotients
					{
						type: "list",
						listData: splitArgIntoNumbers(args[2]),
					},
					// Quotient Sum
					{
						type: "math",
						children: parseMath(addCommas(args[3])),
					},
					// Quotient Steps
					{
						type: "list",
						listData: splitArgIntoMathNodes(args[4]),
					},
				],
			},
		];
	},
	longmult: function(ps) {
		const opt1 = findOptionalArgument(ps);
		const opt2 = findOptionalArgument(ps);
		const args = findArguments(ps, 3);
		const isDigits = opt1 && opt1.text === "digit";
		if (!args) {
			return false;
		}
		return [
			{
				type: "longMultiplication",
				isSum: !!args[2].getRest(),
				isDigits,
				children: [
					{
						type: "list",
						listData: splitArgIntoMathNodes(args[0]),
					},
					{
						type: "list",
						listData: splitArgIntoMathNodes(args[1]),
					},
					{
						type: "math",
						children: parseMath(args[2]),
					},
					{
						type: "list",
						listData: splitArgIntoMathNodes(opt2),
					},
				],
			},
		];
	},
	longadd: function(ps) {
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		return [
			{
				type: "longSimpleOperation",
				operator: "+",
				children: [
					{
						type: "math",
						children: splitArgIntoMathNodes(args[0]),
					},
					{
						type: "math",
						children: parseMath(args[1]),
					},
				],
			},
		];
	},
	longsub: function(ps) {
		const args = findArguments(ps, 2);
		if (!args) {
			return false;
		}
		return [
			{
				type: "longSimpleOperation",
				operator: "\u2212", //The wide dash not -
				children: [
					{
						type: "math",
						children: splitArgIntoMathNodes(args[0]),
					},
					{
						type: "math",
						children: parseMath(args[1]),
					},
				],
			},
		];
	},
	numberAverage: function(ps) {
		//Take in arguments
		let optArg = findOptionalArgument(ps); //allow users to define the differences
		let isCustom = false;
		let hasNoBrace = false;
		let noBoldOnLast = false;
		while (optArg) {
			optArg = optArg.text.trim().toLowerCase();
			if (optArg === "nobrace") hasNoBrace = true;
			if (optArg === "custom") isCustom = true;
			if (optArg === "nobold") noBoldOnLast = true;
			optArg = findOptionalArgument(ps);
		}

		//Take in arguments based on the optional arguments used
		let differences = [];
		let shift = 0;

		//attaches a positive sign to positive numbers for the differences and shift
		const makeNode = function(element) {
			const signed = element > 0 ? "+" + parseInt(element) : "" + element;
			return makeNonmathArgNode(ps.withText(signed));
		};

		//Take in arguments and process them
		const args = findArguments(ps, isCustom ? 4 : 3);
		const givenNumbers = splitArgIntoMathNodes(args[0]);
		const labels = splitArgIntoNonmathNodes(args[1]);
		if (isCustom) {
			differences = splitArgIntoMathNodes(args[2]);
			shift = args[3].text;
		} else {
			const desiredAvg = parseInt(args[2].text);
			//calculate the differences and the overall shift when there are no blanks
			differences = givenNumbers.map((val, i) => {
				const diff = parseInt(givenNumbers[i].children[0].text) - desiredAvg;
				shift += diff;
				return makeNode(diff);
			});
		}
		shift = makeNode(shift);

		//make last givenNumber node bold if there is no brace
		if (hasNoBrace && !noBoldOnLast) {
			const boldedNum = givenNumbers[givenNumbers.length - 1].children[0].text;
			givenNumbers[givenNumbers.length - 1] = makeFormattedText(
				"bold",
				ps.withText(boldedNum)
			)[0];
		}
		if (!args) {
			return false;
		}
		return [
			{
				type: "numberAverage",
				givenNumbers,
				differences,
				shift,
				labels,
				hasNoBrace,
				children: [],
			},
		];
	},
	circlecrop: function(ps) {
		const options = {
			glass: false,
		};
		let optArg;
		// eslint-disable-next-line no-cond-assign
		while ((optArg = findOptionalArgument(ps))) {
			const text = optArg.text;
			if (text === "glass") {
				options.glass = true;
			} else {
				ps.pushError(`Unrecognized optional argument: "${text}"`);
			}
		}

		const args = findArguments(ps, 1);
		if (!args || args[0].getRest() === "") {
			ps.pushError(
				"The circle crop command requires an inner command as well."
			);
			return false;
		}
		return [
			{
				type: "circleCrop",
				options,
				children: parseMath(args[0]),
			},
		];
	},
	begin: function(ps) {
		return parseEnvironment(ps, true);
	},
	end: function(ps) {
		return nonmathCommandHandlers.end(ps);
	},
	underlabel: function(ps) {
		return makeMathCommandNode("underlabel", findArguments(ps, 2));
	},
	...CommandIndex.parsers.mathCommands,
};

const getCustomCommandHandler = function(ps, name) {
	const customData = _.get(ps, ["data", "customCommands", name]);
	if (!customData) {
		return;
	}

	return function(commandPs) {
		const args = findArguments(commandPs, customData.argCount || 0, {
			requireBrace: true,
			noSpace: true,
		});
		if (!args) {
			return false;
		}
		const argsText = args.map(argPs => argPs.getRest());
		let children = [];
		if (customData.childrenArguments) {
			children = argsText.map(function(arg) {
				return {
					type: "math",
					children: parseMath(arg),
				};
			});
		}
		return [
			{
				type: "customCommand",
				name: name,
				args: argsText,
				children: children,
			},
		];
	};
};

// Object mapping environment names to functions taking a parser state, which
// contains only the environment body. For convenience of writing these, the
// parser state is assumed to be always an object and never a string (so no need
// for PS.convert). The parser state is thrown away except for recorded errors
// after this is done, so don't worry about setting its position.
// These functions may return false to signify environment parsing failed.
const ALIGN_RA_ARGS = Object.freeze(["read", "r"]);
const nonmathEnvironmentHandlers = {
	align: function(ps) {
		const optArg0 = Helpers.findOptionalArgument(ps);

		const read = optArg0 && ALIGN_RA_ARGS.includes(optArg0.text); // whether to read aloud
		if (optArg0 && !ALIGN_RA_ARGS.includes(optArg0.text)) {
			ps.pushError(
				`align enviroment optional arg ${optArg0} invalid and must be one of ${ALIGN_RA_ARGS}`
			);
		}

		const linePsList = Helpers.splitByDelimIntoPs(ps, {
			type: "break",
			begin: "\\\\",
		});

		const lineNodes = linePsList.map(function(linePs) {
			const partPsList = Helpers.splitByDelimIntoPs(linePs, {
				type: "ampersand",
				begin: "&",
				escape: "\\",
			});

			if (partPsList.length < 2) {
				partPsList[1] = linePs.withText("");
			} else if (partPsList.length > 2) {
				linePs.pushError("align environment line had more than one ampersand.");
				return {
					type: "error",
					name: "align line",
				};
			}

			const firstPart = makeMathArgNode(partPsList[0]);
			if (!firstPart.children || firstPart.children.length !== 1) {
				// Not a single node before the ampersand.
			} else if (firstPart.children[0].type === "operator") {
				firstPart.children[0].isUnary = false;
			}

			// Operators coming just after the ampersand should be treated as not
			// unary. Artifically add a 1 before it.
			const secondPs = partPsList[1]
				.withText("1" + partPsList[1].getRest(), 1)
				.pipeErrorsAt(1);

			return {
				type: "alignLine",
				children: [firstPart, makeMathArgNode(secondPs)],
				read,
			};
		});

		return [
			{
				type: "alignMath",
				children: lineNodes,
				read,
			},
		];
	},
	"align*": function(ps) {
		return nonmathEnvironmentHandlers.align(ps);
	},
	itemize: function(ps) {
		const optArg = findOptionalArgument(ps);
		let itemPsList = Helpers.splitByDelimIntoPs(ps, {
			type: "item",
			begin: "\\item",
		});
		itemPsList = _.drop(itemPsList, 1);

		const itemNodes = itemPsList.map(function(itemPs) {
			itemPs.text.trim();
			return {
				type: "item",
				children: [
					{
						type: "wrappingItem",
						children: makeNonmathArgNode(itemPs).children,
					},
				],
			};
		});
		return [
			{
				type: "itemList",
				useBullets:
					optArg.text && optArg.text.toLowerCase() === "nb" ? false : true,
				children: itemNodes,
			},
		];
	},
	tabular: function(ps) {
		const optArg0 = findOptionalArgument(ps);
		const optArg1 = findOptionalArgument(ps);

		const arg0 = optArg0 && optArg0.text;
		const arg1 = optArg1 && optArg1.text;

		const mismo = arg0 === "mismo";
		const read = ["r", "read"].includes(arg1);
		let vAlign;
		if (!mismo) {
			vAlign = arg0;
		}
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return parseTable(ps, args[0], makeNonmathArgNode, {vAlign, mismo, read});
	},
	mistake: function(ps) {
		return [
			{
				type: "mistake",
				children: [{type: "wrapping", children: parseNonmath(ps)}],
			},
		];
	},
	tabularmath: function(ps) {
		const optArg0 = findOptionalArgument(ps);
		const optArg1 = findOptionalArgument(ps);
		const vAlign = optArg0 && optArg0.text;
		const read = optArg1 && ["r", "read"].includes(optArg1.text);
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return parseTable(ps, args[0], makeMathArgNode, {vAlign, read});
	},
	tabulareq: function(ps) {
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		return parseTable(ps, args[0], makeMathArgNode, {xPadding: 0});
	},
	optree: function(ps) {
		const options = {align: "center"};
		let makeArgNode = makeMathArgNode;

		let optArg;
		do {
			if (optArg) {
				if (optArg.text === "raw") {
					options.raw = true;
					makeArgNode = makeNonmathArgNode;
				} else if (optArg.text === "center") {
					options.center = true;
				} else if (optArg.text === "leftalign") {
					options.align = "left";
				} else if (optArg.text === "rightalign") {
					options.align = "right";
				} else {
					ps.pushError(
						'The optree environment only supports "raw", "center", "leftalign", and "rightalign" as optional arguments, not ' +
							optArg.text
					);
				}
			}
			optArg = findOptionalArgument(ps);
		} while (optArg);

		if (options.center && options.align !== "center") {
			ps.pushError(
				`Using optree's "center" mode ignores usage of the "${options.align}"align argument`
			);
		}

		return parseOptree(ps, [], makeArgNode, options);
	},
	diagram: function(ps) {
		return parseDiagram(ps);
	},
	numberline: function(ps) {
		const args = findArguments(ps, 1);
		if (!args) {
			ps.pushError(
				"Numberline environments require the range directly after the begin command. \nRange should be two numbers separated by semicolons in curly brackets like {0;1}."
			);
			return false;
		}
		return parseNumberline(ps, args[0], makeMathArgNode);
	},
	...CommandIndex.parsers.nonmathEnvironments,
};
const mathEnvironmentHandlers = {
	...CommandIndex.parsers.mathEnvironments,
};

const getCustomEnvironmentHandler = function(ps, name) {
	const customData = _.get(ps, ["data", "customEnvironments", name]);
	if (!customData) {
		return;
	}

	return function(bodyPs) {
		const args = findArguments(bodyPs, customData.argCount || 0, {
			requireBrace: true,
		});

		if (!args) {
			return false;
		}

		const argsText = args.map(argPs => argPs.getRest());
		const state = findCustomConfigCommands(bodyPs, customData.configCommands);
		const isInline = customData.isInline;
		return [
			{
				type: isInline ? "customInlineEnvironment" : "customEnvironment",
				name,
				body: bodyPs.getRest().trim(),
				args: argsText,
				state,
			},
		];
	};
};

const mapOptionalArgsPSToObject = function(optionalArgsPS) {
	const obj = {};
	if (!optionalArgsPS || optionalArgsPS.length === 0) {
		return obj;
	}
	_.forEach(optionalArgsPS, ps => {
		const groups = /^(.+)\=(.+)$/i.exec(ps.getRest());
		if (
			groups &&
			groups.length > 2 &&
			groups[1].replace(/\s+/g, "") &&
			groups[2].replace(/\s+/g, "")
		) {
			obj[groups[1].replace(/\s+/g, "")] = groups[2].trim();
		}
	});
	return obj;
};

const findCustomConfigCommands = function(ps, configCommands) {
	const commandNames = Object.keys(configCommands || {});
	if (!commandNames.length) {
		return {};
	}
	const pattern = new RegExp("^\\s*\\\\(" + commandNames.join("|") + ")");
	const finalState = {};
	while (true) {
		const result = pattern.exec(ps.getRest());
		if (!result) {
			break;
		}
		ps.pos += result[0].length;
		const commandName = result[1];
		const commandPos = ps.pos - commandName.length - 1;
		// Parsing of optional arguments of form [key=value]
		const optionalPs = [];
		let optPs = findOptionalArgument(ps, {noSpace: true});
		while (optPs) {
			optionalPs.push(optPs);
			optPs = findOptionalArgument(ps, {noSpace: true});
		}
		const optionalArgs = mapOptionalArgsPSToObject(optionalPs);
		// Parsing of command arguments
		const argsPs = findSomeArguments(ps, {noSpace: true});
		const pushError = message => ps.pushError(message, commandPos);
		const configArgs = argsPs.map(argPs => argPs.getRest());
		_.extend(
			finalState,
			configCommands[commandName](pushError, configArgs, optionalArgs)
		);
	}

	return finalState;
};

module.exports = {
	parseBatex: parseEntryNonmath,
	parseMathBatex: parseEntryMath,
};
